import React from 'react';
import { Layers, Layout, Settings, ArrowRight } from 'lucide-react';
import ElementsPanel from './ElementsPanel';
import AssessmentItemsPanel from './AssessmentItemsPanel';
import PropertiesPanel from './PropertiesPanel';
import LayoutPanel from './LayoutPanel';
import NavigationPanel from './NavigationPanel';

const SidebarPanel = ({
  position, // 'left' | 'right' | 'bottom'
  sidebarWidth,
  activePanel,
  setActivePanel,
  selectedElement,
  selectedElements,
  onDeleteSelected,
  handleSidebarResizeStart,
  handleSidebarResizeKeyDown,
  // Elements Panel props
  setDraggingFromSidebar,
  setDragPreviewPosition,
  // Assessment Items Panel props
  items,
  screens,
  currentScreenIndex,
  setShowItemBank,
  addElementToScreen,
  removeAssessmentItem,
  removeAllItems,
  onEditItem,
  // Properties Panel props
  selectedElementData,
  setScreens,
  setConfirmDialog,
  calculateQuestionHeight,
  updateElementContent,
  distributeOptions,
  alignOptions,
  toggleLockElement,
  isElementLocked,
  deleteElement,
  ungroupQuestion,
  regroupQuestion,
  updateSubElementPosition,
  // Layout Panel props
  buttonTemplates,
  selectedButtonTemplate,
  handleLoadButtonTemplate,
  handleSaveButtonTemplate,
  handleDeleteButtonTemplate,
  handleApplyTemplateToAllScreens,
  newTemplateName,
  setNewTemplateName,
  templateSaveLevel,
  setTemplateSaveLevel,
  setTemplateAsDefault,
  setSetTemplateAsDefault,
  updateElementPosition,
  updateElementSize,
  allTemplatesCache,
  refreshTemplatesCache,
  appliedDefaultTemplateIds,
  // Navigation Panel props
  navigationRules,
  setNavigationRules,
  setShowRuleEditorModal,
  setEditingRule,
  // Assessment ID for template fetching
  assessmentId,
  // Device mode for calculating centered positions + canvas preset (Layout tab)
  deviceMode,
  deviceDimensions,
  handleDeviceModeChange,
  // Selection handlers for navigation panel
  setSelectedElement,
  setCurrentScreenIndex,
  // Manual-save handler so LayoutPanel bulk-apply can persist screens (FECOA-1337)
  handleSave,
  // Callback when a default template is set from LayoutPanel
  onDefaultTemplateSet,
  // Form-wide formatting (font family, base size, line spacing) — FECOA-732
  globalFormatting,
  onGlobalFormattingChange,
}) => {
  const isBottom = position === 'bottom';
  const isLeft = position === 'left';
  const isRight = position === 'right';

  const tabs = [
    { id: 'elements', label: 'Elements', icon: Layers, alwaysEnabled: true },
    { id: 'layout', label: 'Layout', icon: Layout, alwaysEnabled: true },
    { id: 'properties', label: 'Properties', icon: Settings, alwaysEnabled: true },
    { id: 'navigation', label: 'Navigation', icon: ArrowRight, alwaysEnabled: true },
  ];

  return (
    <div 
      className={`sidebar-panel bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex shadow-xl dark:shadow-gray-900/20 z-10 relative overflow-hidden ${
        isBottom ? 'h-64 border-t flex-row overflow-x-auto' : 
        isLeft ? 'border-r flex-col' : 
        'border-l flex-col'
      }`}
      style={!isBottom ? { width: `${sidebarWidth}px` } : {}}
    >
      {/* Sidebar Tabs */}
      <div className={`bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex ${
        isBottom ? 'flex-col border-r border-gray-200 dark:border-gray-700 min-w-fit' : 'flex-row w-full'
      }`}>
        {tabs.map(({ id, label, icon: Icon, alwaysEnabled }) => {
          const isActive = activePanel === id;
          const isDisabled = !alwaysEnabled && !selectedElement;
          
          return (
            <button
              key={id}
              onClick={() => setActivePanel(id)}
              disabled={isDisabled}
              className={`${isBottom ? 'py-4 px-3' : 'flex-1 py-3 px-4'} text-xs font-medium transition-all duration-200 relative ${
                isActive
                  ? 'bg-white dark:bg-gray-700 text-primary-600 shadow-sm dark:shadow-gray-900/20'
                  : isDisabled 
                    ? 'text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed'
                    : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-white/50 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className={`p-1.5 rounded-lg mx-auto mb-1.5 w-fit transition-colors duration-200 ${
                isActive ? 'bg-primary-100 dark:bg-primary-900/30' : ''
              }`}>
                <Icon className={`w-4 h-4 ${isActive ? 'text-primary-600' : ''}`} />
              </div>
              <span className={isBottom ? 'text-[10px]' : ''}>{label}</span>
              {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />}
            </button>
          );
        })}
      </div>

      {/* Panel Content */}
      <div className="flex-1 overflow-y-auto overflow-x-auto bg-gray-50 dark:bg-gray-900">
        {activePanel === 'elements' ? (
          <div className="p-4 space-y-5">
            <ElementsPanel
              setDraggingFromSidebar={setDraggingFromSidebar}
              setDragPreviewPosition={setDragPreviewPosition}
            />
            <AssessmentItemsPanel
              items={items}
              screens={screens}
              currentScreenIndex={currentScreenIndex}
              selectedElement={selectedElement}
              setSelectedElement={setSelectedElement}
              setShowItemBank={setShowItemBank}
              setDraggingFromSidebar={setDraggingFromSidebar}
              setDragPreviewPosition={setDragPreviewPosition}
              addElementToScreen={addElementToScreen}
              removeAssessmentItem={removeAssessmentItem}
              removeAllItems={removeAllItems}
              onEditItem={onEditItem}
            />
          </div>
        ) : activePanel === 'layout' ? (
          <LayoutPanel
            selectedElementData={selectedElementData}
            selectedElement={selectedElement}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setScreens={setScreens}
            buttonTemplates={buttonTemplates}
            selectedButtonTemplate={selectedButtonTemplate}
            handleLoadButtonTemplate={handleLoadButtonTemplate}
            handleSaveButtonTemplate={handleSaveButtonTemplate}
            handleDeleteButtonTemplate={handleDeleteButtonTemplate}
            handleApplyTemplateToAllScreens={handleApplyTemplateToAllScreens}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            templateSaveLevel={templateSaveLevel}
            setTemplateSaveLevel={setTemplateSaveLevel}
            setTemplateAsDefault={setTemplateAsDefault}
            setSetTemplateAsDefault={setSetTemplateAsDefault}
            updateElementPosition={updateElementPosition}
            updateElementSize={updateElementSize}
            calculateQuestionHeight={calculateQuestionHeight}
            distributeOptions={distributeOptions}
            alignOptions={alignOptions}
            assessmentId={assessmentId}
            deviceMode={deviceMode}
            deviceDimensions={deviceDimensions}
            handleDeviceModeChange={handleDeviceModeChange}
            allTemplatesCache={allTemplatesCache}
            refreshTemplatesCache={refreshTemplatesCache}
            appliedDefaultTemplateIds={appliedDefaultTemplateIds}
            handleSave={handleSave}
            onDefaultTemplateSet={onDefaultTemplateSet}
            globalFormatting={globalFormatting}
            onGlobalFormattingChange={onGlobalFormattingChange}
          />
        ) : activePanel === 'navigation' ? (
          <NavigationPanel
            selectedElementData={selectedElementData}
            selectedElement={selectedElement}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setScreens={setScreens}
            navigationRules={navigationRules}
            setNavigationRules={setNavigationRules}
            setShowRuleEditorModal={setShowRuleEditorModal}
            setEditingRule={setEditingRule}
            setConfirmDialog={setConfirmDialog}
            setSelectedElement={setSelectedElement}
            setCurrentScreenIndex={setCurrentScreenIndex}
          />
        ) : activePanel === 'properties' ? (
          (selectedElements?.size ?? 0) > 1 ? (
            // Multi-select: show a compact group panel instead of the
            // single-element editor (FECOA-1523).
            <div className="p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Settings className="w-4 h-4 text-primary-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {selectedElements.size} items selected
                </h3>
              </div>
              <p className="text-xs text-gray-600 mb-4">
                Drag any selected item to move the group. Use the arrow keys to nudge,
                Ctrl/Cmd+C then Ctrl/Cmd+V to copy, or delete them all at once.
              </p>
              <button
                onClick={onDeleteSelected}
                className="w-full inline-flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Delete selected
              </button>
            </div>
          ) : (
          <PropertiesPanel
            selectedElementData={selectedElementData}
            selectedElement={selectedElement}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setScreens={setScreens}
            setConfirmDialog={setConfirmDialog}
            calculateQuestionHeight={calculateQuestionHeight}
            updateElementContent={updateElementContent}
            distributeOptions={distributeOptions}
            alignOptions={alignOptions}
            toggleLockElement={toggleLockElement}
            isElementLocked={isElementLocked}
            deleteElement={deleteElement}
            ungroupQuestion={ungroupQuestion}
            regroupQuestion={regroupQuestion}
            updateSubElementPosition={updateSubElementPosition}
          />
          )
        ) : null}
      </div>
      
      {/* Resize Handle */}
      {!isBottom && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={sidebarWidth}
          aria-valuemin={320}
          aria-valuemax={600}
          tabIndex={0}
          className={`absolute top-0 bottom-0 w-2 cursor-col-resize bg-gray-200 dark:bg-gray-700 hover:bg-primary-500 hover:w-2.5 focus-visible:bg-primary-500 focus-visible:outline-none transition-all z-50 group ${
            isLeft ? 'right-0' : 'left-0'
          }`}
          onMouseDown={handleSidebarResizeStart}
          onKeyDown={handleSidebarResizeKeyDown}
          title="Drag or use arrow keys to resize"
        >
          {/* Invisible hit area, biased toward the canvas so it doesn't cover the sidebar scrollbar */}
          <div className={`absolute inset-y-0 w-10 ${isLeft ? 'right-0 -mr-7' : 'left-0 -ml-7'}`} />
          <div className={`absolute top-1/2 -translate-y-1/2 w-2 h-20 bg-gray-400 dark:bg-gray-500 opacity-60 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity rounded-full ${
            isLeft ? 'right-0' : 'left-0'
          }`} />
        </div>
      )}
    </div>
  );
};

export default SidebarPanel;
